import { useEffect, useRef, useState } from "react";
import { usePostHog } from "posthog-js/react";
import "./index.css";
import alexSuprunJpg from "../public/alex-suprun-ZHvM3XIOHoE-unsplash.jpg";
// Hero avatars
import alexSuprunWebp from "../public/alex-suprun-ZHvM3XIOHoE-unsplash-400w.webp";
import benDenEngelsenJpg from "../public/ben-den-engelsen-M0HGxrpWmmg-unsplash.jpg";
import benDenEngelsenWebp from "../public/ben-den-engelsen-M0HGxrpWmmg-unsplash-400w.webp";
import bywardOutfittersJpg from "../public/byward-outfitters-waUHEtx8UG4-unsplash.jpg";
import bywardOutfittersWebp from "../public/byward-outfitters-waUHEtx8UG4-unsplash-400w.webp";
import cesarRinconJpg from "../public/cesar-rincon-XHVpWcr5grQ-unsplash.jpg";
import cesarRinconWebp from "../public/cesar-rincon-XHVpWcr5grQ-unsplash-400w.webp";
import feature01 from "../public/feature01.png";
// Feature images
import feature01_400w from "../public/feature01-400w.png";
import feature01_800w from "../public/feature01-800w.png";
import feature01_1200w from "../public/feature01-1200w.png";
import feature02 from "../public/feature02.png";
import feature02_400w from "../public/feature02-400w.png";
import feature02_800w from "../public/feature02-800w.png";
import feature02_1200w from "../public/feature02-1200w.png";
import feature03 from "../public/feature03.png";
import feature03_400w from "../public/feature03-400w.png";
import feature03_800w from "../public/feature03-800w.png";
import feature03_1200w from "../public/feature03-1200w.png";
import juricaKoleticJpg from "../public/jurica-koletic-7YVZYZeITc8-unsplash.jpg";
import juricaKoleticWebp from "../public/jurica-koletic-7YVZYZeITc8-unsplash-400w.webp";
// Background pattern
import maskGroupPattern from "../public/mask-group-pattern.svg";
import rachelMcdermottJpg from "../public/rachel-mcdermott-0fN7Fxv1eWA-unsplash.jpg";
import rachelMcdermottWebp from "../public/rachel-mcdermott-0fN7Fxv1eWA-unsplash-400w.webp";
import FAQSection from "./components/FAQSection";
import { FeatureCard } from "./components/FeatureCard";
import { Footer } from "./components/Footer";
import { Header } from "./components/Header";
import { Layout } from "./components/Layout";
import { PricingSection } from "./components/PricingSection";
import ProblemsSection from "./components/ProblemsSection";
import { Input } from "./components/ui/input";

export function App() {
	const [activeCard, setActiveCard] = useState(0);
	const [progress, setProgress] = useState(0);
	const hasIncrementedRef = useRef(false);
	const emailInputRef = useRef<HTMLInputElement>(null);
	const posthog = usePostHog();

	useEffect(() => {
		const progressInterval = setInterval(() => {
			setProgress((prev) => {
				const newProgress = prev + 2; // 2% every 100ms = 5 seconds total

				if (newProgress >= 100) {
					// Only increment once per cycle
					if (!hasIncrementedRef.current) {
						hasIncrementedRef.current = true;
						setActiveCard((current) => (current + 1) % 3);
						// Reset flag after a small delay
						setTimeout(() => {
							hasIncrementedRef.current = false;
						}, 200);
					}
					return 0;
				}

				return newProgress;
			});
		}, 100);

		return () => {
			clearInterval(progressInterval);
		};
	}, []);

	const handleCardClick = (index: number) => {
		setActiveCard(index);
		setProgress(0);
		hasIncrementedRef.current = false;
	};

	const handleJoinWaitlist = () => {
		const email = emailInputRef.current?.value?.trim();

		// Track PostHog event
		posthog?.capture("join_waitlist_clicked", {
			email_provided: !!email,
			email: email || undefined,
		});

		const baseUrl = "https://tally.so/r/worLLN";
		const url = email
			? `${baseUrl}?email=${encodeURIComponent(email)}`
			: baseUrl;
		window.location.href = url;
	};

	return (
		<Layout>
			<div className="self-stretch pt-[9px] overflow-hidden border-b border-[rgba(55,50,47,0.06)] flex flex-col justify-center items-center gap-4 sm:gap-6 md:gap-8 lg:gap-[66px] relative z-10">
				{/* Header */}
				<Header onJoinWaitlist={handleJoinWaitlist} />

				{/* Hero Section */}
				<div className="pt-16 sm:pt-20 md:pt-24 lg:pt-[180px] pb-8 sm:pb-12 md:pb-16 flex flex-col justify-start items-center px-2 sm:px-4 md:px-8 lg:px-0 w-full">
					<div className="w-full max-w-[937px] lg:w-[937px] flex flex-col justify-center items-center gap-3 sm:gap-4 md:gap-5 lg:gap-6">
						<div className="self-stretch rounded-[3px] flex flex-col justify-center items-center gap-4 sm:gap-5 md:gap-6 lg:gap-8">
							<h1 className="w-full max-w-[748.71px] lg:w-[748.71px] text-center flex justify-center flex-col text-[#37322F] text-[28px] sm:text-[36px] md:text-[52px] lg:text-[80px] font-normal leading-[1.1] sm:leading-[1.15] md:leading-[1.2] lg:leading-24 font-serif px-2 sm:px-4 md:px-0">
								Finally, a Running Buddy You Can Ask Anything
							</h1>
							<div className="w-full max-w-[506.08px] lg:w-[506.08px] text-center flex justify-center flex-col text-[rgba(55,50,47,0.80)] sm:text-lg md:text-xl leading-[1.4] sm:leading-[1.45] md:leading-[1.5] lg:leading-7 font-sans px-2 sm:px-4 md:px-0 lg:text-lg font-medium text-sm">
								Tired today? Wondering about your pace? Just want to talk
								through your training plan? Chat naturally and get personalized
								answers based on your real data and goals.
							</div>
						</div>
					</div>

					<div className="w-full max-w-[600px] lg:w-[600px] flex flex-col justify-center items-center gap-6 sm:gap-8 relative z-10 mt-6 sm:mt-8 md:mt-10 lg:mt-12">
						{/* Email Input + Button */}
						<div className="w-full backdrop-blur-[8.25px] flex flex-col sm:flex-row justify-center items-stretch gap-3 px-4 sm:px-0">
							<Input
								ref={emailInputRef}
								type="email"
								placeholder="Enter your email"
								className="flex-1 h-10 sm:h-11 md:h-12 py-2.5 sm:py-1 px-4 sm:px-5 md:px-6 text-sm sm:text-base bg-white border border-[rgba(55,50,47,0.12)] rounded-full shadow-sm focus:border-[#37322F] focus:ring-2 focus:ring-[rgba(55,50,47,0.1)]"
							/>
							<button
								type="button"
								onClick={handleJoinWaitlist}
								className="h-10 sm:h-11 md:h-12 px-6 sm:px-8 md:px-10 lg:px-12 sm:py-[6px] relative bg-[#37322F] shadow-[0px_0px_0px_2.5px_rgba(255,255,255,0.08)_inset] overflow-hidden rounded-full flex justify-center items-center whitespace-nowrap cursor-pointer"
							>
								<div className="w-20 sm:w-24 md:w-28 lg:w-44 h-[41px] absolute left-0 top-[-0.5px] bg-gradient-to-b from-[rgba(255,255,255,0)] to-[rgba(0,0,0,0.10)] mix-blend-multiply"></div>
								<div className="flex flex-col justify-center text-white text-sm sm:text-base md:text-[15px] font-medium leading-5 font-sans">
									Join the Waitlist
								</div>
							</button>
						</div>

						{/* Social Proof */}
						<div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4">
							<div className="flex -space-x-3">
								<div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border-2 border-white overflow-hidden shadow-md">
									<picture>
										<source srcSet={alexSuprunWebp} type="image/webp" />
										<img
											src={alexSuprunJpg}
											alt="User avatar"
											className="w-full h-full object-cover"
											width="640"
											height="640"
										/>
									</picture>
								</div>
								<div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border-2 border-white overflow-hidden shadow-md">
									<picture>
										<source srcSet={rachelMcdermottWebp} type="image/webp" />
										<img
											src={rachelMcdermottJpg}
											alt="User avatar"
											className="w-full h-full object-cover"
											width="640"
											height="499"
										/>
									</picture>
								</div>
								<div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border-2 border-white overflow-hidden shadow-md">
									<picture>
										<source srcSet={benDenEngelsenWebp} type="image/webp" />
										<img
											src={benDenEngelsenJpg}
											alt="User avatar"
											className="w-full h-full object-cover"
											width="640"
											height="427"
										/>
									</picture>
								</div>
								<div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border-2 border-white overflow-hidden shadow-md">
									<picture>
										<source srcSet={cesarRinconWebp} type="image/webp" />
										<img
											src={cesarRinconJpg}
											alt="User avatar"
											className="w-full h-full object-cover"
											width="640"
											height="640"
										/>
									</picture>
								</div>
								<div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border-2 border-white overflow-hidden shadow-md">
									<picture>
										<source srcSet={juricaKoleticWebp} type="image/webp" />
										<img
											src={juricaKoleticJpg}
											alt="User avatar"
											className="w-full h-full object-cover"
											width="640"
											height="960"
										/>
									</picture>
								</div>
								<div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border-2 border-white overflow-hidden shadow-md">
									<picture>
										<source srcSet={bywardOutfittersWebp} type="image/webp" />
										<img
											src={bywardOutfittersJpg}
											alt="User avatar"
											className="w-full h-full object-cover"
											width="640"
											height="959"
										/>
									</picture>
								</div>
							</div>
							<div className="text-[rgba(55,50,47,0.65)] text-sm sm:text-base font-medium">
								+100 people already joined
							</div>
						</div>
					</div>

					{/* Background pattern */}
					<div className="absolute top-0 left-1/2 transform -translate-x-1/2 z-0 pointer-events-none">
						<img
							src={maskGroupPattern}
							alt=""
							className="w-[936px] sm:w-[1404px] md:w-[2106px] lg:w-[2808px] h-auto opacity-30 sm:opacity-40 md:opacity-50 mix-blend-multiply"
							style={{
								filter: "hue-rotate(15deg) saturate(0.7) brightness(1.2)",
							}}
						/>
					</div>

					{/* Dashboard with Feature Images */}
					<div className="w-full max-w-[960px] lg:w-[960px] pt-2 sm:pt-4 pb-6 sm:pb-8 md:pb-10 px-2 sm:px-4 md:px-6 lg:px-11 flex flex-col justify-center items-center gap-2 relative z-5 my-8 sm:my-12 md:my-16 lg:my-16 mb-0 lg:pb-0">
						<div className="w-full max-w-[960px] lg:w-[960px] h-[200px] sm:h-[280px] md:h-[450px] lg:h-[695.55px] bg-white shadow-[0px_0px_0px_0.9056603908538818px_rgba(0,0,0,0.08)] overflow-hidden rounded-[6px] sm:rounded-[8px] lg:rounded-[9.06px] flex flex-col justify-start items-start">
							{/* Dashboard Content */}
							<div className="self-stretch flex-1 flex justify-start items-start">
								{/* Main Content */}
								<div className="w-full h-full flex items-center justify-center">
									<div className="relative w-full h-full overflow-hidden">
										{/* Product Image 3 - Data visualization */}
										<div
											className={`absolute inset-0 transition-all duration-500 ease-in-out ${
												activeCard === 0
													? "opacity-100 scale-100 blur-0"
													: "opacity-0 scale-95 blur-sm"
											}`}
										>
											<picture>
												<source
													srcSet={`${feature03_400w} 400w, ${feature03_800w} 800w, ${feature03_1200w} 1200w`}
													sizes="(max-width: 768px) 100vw, 960px"
													type="image/png"
												/>
												<img
													src={feature03}
													alt="Data Visualization Dashboard"
													className="w-full h-full object-cover"
													width="2560"
													height="1856"
													fetchPriority="high"
												/>
											</picture>
										</div>

										{/* Product Image 2 - Data to insights */}
										<div
											className={`absolute inset-0 transition-all duration-500 ease-in-out ${
												activeCard === 1
													? "opacity-100 scale-100 blur-0"
													: "opacity-0 scale-95 blur-sm"
											}`}
										>
											<picture>
												<source
													srcSet={`${feature02_400w} 400w, ${feature02_800w} 800w, ${feature02_1200w} 1200w`}
													sizes="(max-width: 768px) 100vw, 960px"
													type="image/png"
												/>
												<img
													src={feature02}
													alt="Analytics Dashboard"
													className="w-full h-full object-cover"
													width="2560"
													height="1856"
												/>
											</picture>
										</div>

										{/* Product Image 1 - Plan your schedules */}
										<div
											className={`absolute inset-0 transition-all duration-500 ease-in-out ${
												activeCard === 2
													? "opacity-100 scale-100 blur-0"
													: "opacity-0 scale-95 blur-sm"
											}`}
										>
											<picture>
												<source
													srcSet={`${feature01_400w} 400w, ${feature01_800w} 800w, ${feature01_1200w} 1200w`}
													sizes="(max-width: 768px) 100vw, 960px"
													type="image/png"
												/>
												<img
													src={feature01}
													alt="Schedules Dashboard - Customer Subscription Management"
													className="w-full h-full object-cover"
													width="2560"
													height="1856"
												/>
											</picture>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>

					{/* Feature Cards Section */}
					<div className="self-stretch border-t border-[#E0DEDB] border-b border-[#E0DEDB] flex justify-center items-start">
						<div className="w-4 sm:w-6 md:w-8 lg:w-12 self-stretch relative overflow-hidden">
							{/* Left decorative pattern */}
							<div className="w-[120px] sm:w-[140px] md:w-[162px] left-[-40px] sm:left-[-50px] md:left-[-58px] top-[-120px] absolute flex flex-col justify-start items-start">
								{Array.from({ length: 50 }).map((_, i) => (
									<div
										key={`left-pattern-${i}`}
										className="self-stretch h-3 sm:h-4 rotate-[-45deg] origin-top-left outline outline-[0.5px] outline-[rgba(3,7,18,0.08)] outline-offset-[-0.25px]"
									/>
								))}
							</div>
						</div>

						<div className="flex-1 px-0 sm:px-2 md:px-0 flex flex-col md:flex-row justify-center items-stretch gap-0">
							{/* Feature Cards */}
							<FeatureCard
								title="Chat Like You Mean It"
								description={`"Should I run today?" "Why am I slower this week?" "Is this pain normal?" Talk naturally. Get advice that's actually yours—shaped by your goals, your body, your training block, your real life. It's like texting that friend who always knows what to say.`}
								isActive={activeCard === 0}
								progress={activeCard === 0 ? progress : 0}
								onClick={() => handleCardClick(0)}
							/>
							<FeatureCard
								title="AI Screening That Gets You"
								description="Forget boring questionnaires. Our AI talks to you like a real running buddy would—asking about your goals, your injuries, your running history. Natural. Personal. Human. And it remembers everything that matters, updating your context as your life and training evolve."
								isActive={activeCard === 1}
								progress={activeCard === 1 ? progress : 0}
								onClick={() => handleCardClick(1)}
							/>
							<FeatureCard
								title="Smart Data Sync"
								description="Connect your Garmin, Strava, Coros, Suunto, Polar, or Whoop once. That's it. Every run, every workout, every recovery metric flows automatically. No logging. No manual updates. Just pure, effortless intelligence that knows your training inside out."
								isActive={activeCard === 2}
								progress={activeCard === 2 ? progress : 0}
								onClick={() => handleCardClick(2)}
							/>
						</div>

						<div className="w-4 sm:w-6 md:w-8 lg:w-12 self-stretch relative overflow-hidden">
							{/* Right decorative pattern */}
							<div className="w-[120px] sm:w-[140px] md:w-[162px] left-[-40px] sm:left-[-50px] md:left-[-58px] top-[-120px] absolute flex flex-col justify-start items-start">
								{Array.from({ length: 50 }).map((_, i) => (
									<div
										key={`right-pattern-${i}`}
										className="self-stretch h-3 sm:h-4 rotate-[-45deg] origin-top-left outline outline-[0.5px] outline-[rgba(3,7,18,0.08)] outline-offset-[-0.25px]"
									/>
								))}
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Problems Section */}
			<ProblemsSection />

			{/* Pricing Section */}
			<PricingSection onJoinWaitlist={handleJoinWaitlist} />

			{/* FAQ Section */}
			<FAQSection />

			{/* Footer */}
			<Footer />
		</Layout>
	);
}

export default App;
